import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { VulnerabilitySummaryCards } from "./VulnerabilitySummaryCards";
import { VulnerabilityFilters } from "./VulnerabilityFilters";
import { VulnerabilityTable } from "./VulnerabilityTable";
import { VulnerabilityDetailPanel } from "./VulnerabilityDetailPanel";
import { Vulnerability, VulnerabilitySummary } from "@/types/vulnerability";
import { Progress } from "@/components/ui/progress";

interface AppleUpdate {
  name: string;
  version: string;
  build: string;
  released: string;
  security: boolean;
  actively_exploited?: boolean;
  cve_ids?: string[];
  cve_details?: Array<{
    id: string;
    severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
    score: number;
    description?: string;
  }>;
  os_type: string;
  notes_url: string;
}

export function VulnerabilityDashboard() {
  const [timeRange, setTimeRange] = useState("all");
  const [software, setSoftware] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVulnerability, setSelectedVulnerability] = useState<Vulnerability | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);

  const { data: updatesData, isLoading } = useQuery({
    queryKey: ["apple-updates-vulnerabilities"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-apple-updates');
      if (error) throw error;
      return data?.updates || [];
    },
  });

  const updates = updatesData as AppleUpdate[] || [];

  // Transform updates into vulnerability-centric view
  const vulnerabilities = useMemo(() => {
    const cveMap = new Map<string, Vulnerability>();

    updates.forEach(update => {
      if (update.cve_details && update.cve_details.length > 0) {
        update.cve_details.forEach(cve => {
          if (!cveMap.has(cve.id)) {
            cveMap.set(cve.id, {
              cve_id: cve.id,
              severity: cve.severity,
              cvss_score: cve.score,
              description: cve.description,
              actively_exploited: update.actively_exploited || false,
              software_names: [update.name],
              os_types: [update.os_type],
              published_date: update.released,
              affected_updates: [{
                name: update.name,
                version: update.version,
                os_type: update.os_type,
                released: update.released,
                notes_url: update.notes_url,
              }],
            });
          } else {
            const existing = cveMap.get(cve.id)!;
            if (!existing.software_names.includes(update.name)) {
              existing.software_names.push(update.name);
              existing.os_types.push(update.os_type);
            }
            existing.affected_updates.push({
              name: update.name,
              version: update.version,
              os_type: update.os_type,
              released: update.released,
              notes_url: update.notes_url,
            });
            if (update.actively_exploited) {
              existing.actively_exploited = true;
            }
          }
        });
      }
    });

    return Array.from(cveMap.values()).sort((a, b) => {
      // Sort by severity first, then by CVSS score
      const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, NONE: 4 };
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return b.cvss_score - a.cvss_score;
    });
  }, [updates]);

  // Calculate summary statistics
  const summary: VulnerabilitySummary = useMemo(() => {
    const critical = vulnerabilities.filter(v => v.severity === 'CRITICAL').length;
    const high = vulnerabilities.filter(v => v.severity === 'HIGH').length;
    const medium = vulnerabilities.filter(v => v.severity === 'MEDIUM').length;
    const low = vulnerabilities.filter(v => v.severity === 'LOW').length;
    const actively_exploited_count = vulnerabilities.filter(v => v.actively_exploited).length;

    // Calculate top software
    const softwareMap = new Map<string, { count: number; os_type: string }>();
    vulnerabilities.forEach(v => {
      v.software_names.forEach((name, idx) => {
        const existing = softwareMap.get(name);
        if (existing) {
          existing.count++;
        } else {
          softwareMap.set(name, { count: 1, os_type: v.os_types[idx] || 'macOS' });
        }
      });
    });

    const top_software = Array.from(softwareMap.entries())
      .map(([name, data]) => ({ name, count: data.count, os_type: data.os_type }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      total: vulnerabilities.length,
      critical,
      high,
      medium,
      low,
      actively_exploited_count,
      actively_exploited_percentage: vulnerabilities.length > 0 
        ? (actively_exploited_count / vulnerabilities.length) * 100 
        : 0,
      top_software,
    };
  }, [vulnerabilities]);

  // Filter vulnerabilities
  const filteredVulnerabilities = useMemo(() => {
    return vulnerabilities.filter(vuln => {
      // Time range filter
      if (timeRange !== 'all') {
        const publishedDate = new Date(vuln.published_date);
        const now = new Date();
        const daysDiff = (now.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24);

        if (timeRange === '24h' && daysDiff > 1) return false;
        if (timeRange === '7d' && daysDiff > 7) return false;
        if (timeRange === '30d' && daysDiff > 30) return false;
        if (timeRange === '90d' && daysDiff > 90) return false;
      }

      // Software filter
      if (software !== 'all') {
        const matchesSoftware = vuln.software_names.some(name => 
          name.toLowerCase().includes(software.toLowerCase())
        ) || vuln.os_types.some(os => os.toLowerCase() === software.toLowerCase());
        if (!matchesSoftware) return false;
      }

      // Severity filter
      if (severity !== 'all' && vuln.severity !== severity) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return vuln.cve_id.toLowerCase().includes(query) ||
          vuln.software_names.some(name => name.toLowerCase().includes(query)) ||
          (vuln.description && vuln.description.toLowerCase().includes(query));
      }

      return true;
    });
  }, [vulnerabilities, timeRange, software, severity, searchQuery]);

  const handleRowClick = (vulnerability: Vulnerability) => {
    setSelectedVulnerability(vulnerability);
    setDetailPanelOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Progress value={40} className="w-56 mb-2" />
        <p className="text-sm text-muted-foreground">Loading vulnerabilities...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <VulnerabilitySummaryCards summary={summary} vulnerabilities={vulnerabilities} />
      
      <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl p-6 shadow-md transition-all duration-300">
        <VulnerabilityFilters
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          software={software}
          onSoftwareChange={setSoftware}
          severity={severity}
          onSeverityChange={setSeverity}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </div>

      <VulnerabilityTable
        vulnerabilities={filteredVulnerabilities}
        onRowClick={handleRowClick}
      />

      <VulnerabilityDetailPanel
        vulnerability={selectedVulnerability}
        open={detailPanelOpen}
        onOpenChange={setDetailPanelOpen}
      />
    </div>
  );
}
